import React, { useState, useEffect, useRef } from 'react';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import { AuthProvider } from './context/AuthContext';
import Navbar from './components/Navbar';
import Premium from './components/Premium';
import AnalyticsPremium from './pages/AnalyticsPremium';

const APP_VERSION_KEY = 'app_version';

// Navegación sencilla por estado local para no romper la app actual.
function App() {
    const [page, setPage] = useState('home');
    const [darkMode, setDarkMode] = useState(() => {
        const saved = localStorage.getItem('darkMode');
        return saved ? JSON.parse(saved) : true;
    });
    const dashboardRef = useRef(null);
    const [pendingAction, setPendingAction] = useState(null);
    const [showUpdateBanner, setShowUpdateBanner] = useState(false);
    const [latestVersion, setLatestVersion] = useState(null);

    useEffect(() => {
        document.body.setAttribute('data-theme', darkMode ? 'dark' : 'light');
        localStorage.setItem('darkMode', JSON.stringify(darkMode));
    }, [darkMode]);

    // Detectar si hay una versión nueva de la app (para usuarios con versión antigua en caché)
    useEffect(() => {
        const checkVersion = async () => {
            try {
                const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
                if (!res.ok) return;
                const data = await res.json();
                const serverVersion = data?.version ?? '';
                const storedVersion = localStorage.getItem(APP_VERSION_KEY) ?? '';
                if (!storedVersion) {
                    localStorage.setItem(APP_VERSION_KEY, serverVersion);
                    return;
                }
                if (serverVersion && serverVersion !== storedVersion) {
                    setLatestVersion(serverVersion);
                    setShowUpdateBanner(true);
                }
            } catch {
                // Ignorar errores de red
            }
        };
        checkVersion();
        const interval = setInterval(checkVersion, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const handleUpdateNow = () => {
        if (latestVersion) localStorage.setItem(APP_VERSION_KEY, latestVersion);
        setShowUpdateBanner(false);
        window.location.reload();
    };

    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
    };

    const handleExport = () => {
        if (page !== 'agenda') {
            setPendingAction('export');
            setPage('agenda');
            return;
        }

        if (dashboardRef.current && dashboardRef.current.handleExport) {
            dashboardRef.current.handleExport();
        }
    };

    const handleCopyEmails = () => {
        if (page !== 'agenda') {
            setPendingAction('copyEmails');
            setPage('agenda');
            return;
        }

        if (dashboardRef.current && dashboardRef.current.handleCopyEmails) {
            dashboardRef.current.handleCopyEmails();
        }
    };

    const handleExportAll = () => {
        if (page !== 'agenda') {
            setPendingAction('exportAll');
            setPage('agenda');
            return;
        }

        if (dashboardRef.current && dashboardRef.current.handleExportAll) {
            dashboardRef.current.handleExportAll();
        }
    };

    const handleCopyAllEmails = () => {
        if (page !== 'agenda') {
            setPendingAction('copyAllEmails');
            setPage('agenda');
            return;
        }

        if (dashboardRef.current && dashboardRef.current.handleCopyAllEmails) {
            dashboardRef.current.handleCopyAllEmails();
        }
    };

    useEffect(() => {
        if (page === 'agenda' && dashboardRef.current && pendingAction) {
            if (pendingAction === 'export' && dashboardRef.current.handleExport) {
                dashboardRef.current.handleExport();
            }
            if (pendingAction === 'copyEmails' && dashboardRef.current.handleCopyEmails) {
                dashboardRef.current.handleCopyEmails();
            }
            if (pendingAction === 'exportAll' && dashboardRef.current.handleExportAll) {
                dashboardRef.current.handleExportAll();
            }
            if (pendingAction === 'copyAllEmails' && dashboardRef.current.handleCopyAllEmails) {
                dashboardRef.current.handleCopyAllEmails();
            }
            setPendingAction(null);
        }
    }, [page, pendingAction]);

    return (
        <AuthProvider>
            {showUpdateBanner && (
                <div className="app-update-banner" role="alert">
                    <div className="app-update-banner-content">
                        <span className="app-update-banner-text">
                            Hay una nueva versión de la app. Actualizá para tener las últimas mejoras y correcciones.
                        </span>
                        <button type="button" className="app-update-banner-btn" onClick={handleUpdateNow}>
                            Actualizar ahora
                        </button>
                        <button
                            type="button"
                            className="app-update-banner-dismiss"
                            onClick={() => setShowUpdateBanner(false)}
                            aria-label="Cerrar"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}
            <div className="container">
                <Navbar
                    currentPage={page}
                    onChangePage={setPage}
                    darkMode={darkMode}
                    toggleDarkMode={toggleDarkMode}
                    onExport={handleExport}
                    onCopyEmails={handleCopyEmails}
                    onExportAll={handleExportAll}
                    onCopyAllEmails={handleCopyAllEmails}
                />
                <div className="app">
                    <main className="app-content">
                        {page === 'home' && <Home />}
                        {page === 'agenda' && <Dashboard ref={dashboardRef} />}
                        {page === 'premium' && <Premium />}
                        {page === 'analytics' && <AnalyticsPremium />}
                    </main>
                </div>
            </div>
        </AuthProvider>
    );
}

export default App;
